/**
 * Which emergency call is up right now.
 *
 * Module-level and React-free, mirroring `sos-alert-queue.ts`. It exists so the call outlives the
 * surface that STARTED it: Accept is pressed on the emergency modal, which is mounted per-dashboard
 * and unmounts the moment the operator navigates — to the map to see where the wearer is, to the
 * roster, anywhere. A call that hung up on navigation would be worse than no call, because the
 * operator would not know it had happened.
 *
 * So the modal writes here and an overlay mounted in `AppLayout` reads it. One call at a time, on
 * purpose: an operator cannot speak to two people at once, and offering a second line during an
 * emergency invites exactly the mistake nobody can afford.
 *
 * PRESENT AND LIVE ARE DIFFERENT FACTS, and conflating them was a real bug: a call that had already
 * FAILED stayed in this store, because the only thing that cleared it was the operator noticing the
 * small corner panel and dismissing it by hand. The emergency modal read "present" as "busy", so the
 * NEXT emergency's Accept silently degraded to acknowledging the alarm and placing no call at all.
 *
 * The failed call must stay on screen — `call-copy.ts` exists so the operator reads WHY it failed,
 * and clearing it automatically would erase that at the moment it matters. So the call stays PRESENT
 * and stops being LIVE.
 *
 * Liveness is DERIVED from the phase the panel pushes here, never a second flag a component must
 * remember to set. A flag can drift from what the call is actually doing; a derivation cannot. And
 * because the wrong derivation is worse than the bug — marking a LIVE call settled lets the next
 * emergency displace a conversation in progress — settled is defined by the two phases that
 * unambiguously mean "over", never by the absence of activity. `idle` is NOT terminal: a panel
 * renders it for one commit before `start()` runs, and treating it as over would settle every call
 * at birth.
 */

import type { HelmetCallPhase } from './call-resources'

export interface SosCallTarget {
  /**
   * Identifies THE CALL, not the device.
   *
   * Load-bearing. `robotId` names a helmet, and the same helmet raising a second emergency is the
   * ordinary case — the wearer presses SOS again. Keyed on `robotId`, the overlay reuses the dead
   * panel for that second call, its `startedRef` is already latched, and no call is ever placed
   * while the modal has just promised one. Minted here so no caller can forget it.
   */
  callId: string
  robotId: string
  /** What to call the wearer on screen. Resolved by the caller, which has the roster. */
  label: string
  /** The alarm this call answers, for the record. Not every call has one. */
  alertId?: string
}

/** What a caller supplies. The `callId` is the store's to mint. */
export type SosCallRequest = Omit<SosCallTarget, 'callId'>

type Listener = (target: SosCallTarget | null) => void
type LiveListener = (live: boolean) => void

const listeners = new Set<Listener>()
const liveListeners = new Set<LiveListener>()

let current: SosCallTarget | null = null
/** Null until the panel reports one. Null means "started but not yet reporting" — i.e. LIVE. */
let phase: HelmetCallPhase | null = null
/**
 * The one call whose panel has already dialled, so a remount cannot re-dial a handled emergency.
 *
 * A single id, NOT a set: only one call exists at a time, so a set only ever accumulated ids that
 * could never be claimed again — an unbounded leak in a module that lives for the whole session,
 * and one that made a stale entry silently suppress a later dial.
 */
let dialledCallId: string | null = null
let seq = 0

/**
 * The two phases that mean the call is over.
 *
 * Deliberately NOT `!callIsActive(phase)` and NOT `callStatusCopy(phase).terminal`, both of which
 * are also true for `idle`. Those two answer "should the dismiss X be offered?"; this answers "may
 * another emergency take this line?", and the cost of being wrong is a live call dropped in silence.
 */
function isOver(p: HelmetCallPhase | null): boolean {
  return p !== null && (p.kind === 'ended' || p.kind === 'failed')
}

function isLive(): boolean {
  return current !== null && !isOver(phase)
}

function publish(): void {
  const live = isLive()

  for (const fn of listeners) fn(current)
  for (const fn of liveListeners) fn(live)
}

/**
 * Start a call. Returns false when one is already LIVE.
 *
 * A second request while a call is LIVE is IGNORED rather than queued or swapped. Swapping would
 * drop a live emergency call silently; queueing would ring a second helmet nobody is listening to.
 *
 * A call that is merely PRESENT — already ended or failed, still on screen so its reason can be
 * read — does not block the next emergency. It is replaced.
 */
export function beginSosCall(target: SosCallRequest): boolean {
  if (isLive()) return false

  seq += 1
  current = { ...target, callId: `call-${seq}` }
  dialledCallId = null
  // Reset EXPLICITLY rather than relying on the previous call having cleaned up. A stale `phase`
  // left at a terminal value would make this brand-new call replaceable from birth, and the next
  // emergency would cut off a conversation with someone in distress.
  phase = null
  publish()

  return true
}

/**
 * Report where a call has got to. Ignored for any call but the current one.
 *
 * Guarded on `callId` and not on `robotId`: a replacement can name the same device, and a late push
 * from the previous call would otherwise mark the new one over.
 */
export function setSosCallPhase(callId: string, next: HelmetCallPhase): void {
  if (!current || current.callId !== callId) return

  const wasLive = isLive()

  phase = next
  // Only a change in LIVENESS is worth waking the app for; a starting → ringing transition is the
  // panel's own business and it already has the phase.
  if (wasLive !== isLive()) publish()
}

/**
 * Claim the right to dial `callId`, once.
 *
 * The panel dials itself on mount, which is right — the operator already pressed Accept. But the
 * overlay lives in `AppLayout`, and anything that unmounts it (logout, a route outside the layout,
 * an error boundary) remounts a fresh panel over the SAME stored call. Without this latch that
 * panel would ring the helmet again for an emergency handled minutes ago, and call `getUserMedia`
 * outside any user gesture while doing it.
 */
export function claimSosCallDial(callId: string): boolean {
  if (dialledCallId === callId) return false
  dialledCallId = callId

  return true
}

/** End the call — hang-up, failure, or the operator dismissing a dead one. Idempotent. */
export function endSosCall(): void {
  if (!current) return
  dialledCallId = null
  current = null
  phase = null
  publish()
}

export function subscribeSosCall(fn: Listener): () => void {
  listeners.add(fn)
  fn(current)

  return () => {
    listeners.delete(fn)
  }
}

/**
 * Subscribe to LIVENESS alone.
 *
 * Separate from {@link subscribeSosCall} rather than widening its payload, because the two
 * consumers want different facts: the overlay renders whatever is PRESENT, and the emergency modal
 * only ever asks "may I place a call?". Replays synchronously on subscribe, exactly as
 * `subscribeSosCall` does — a modal that mounts mid-call must know before its first render, or it
 * offers a call it cannot place.
 */
export function subscribeSosCallLive(fn: LiveListener): () => void {
  liveListeners.add(fn)
  fn(isLive())

  return () => {
    liveListeners.delete(fn)
  }
}

/** The call in progress, for a caller that needs to ask rather than subscribe. */
export function readSosCall(): SosCallTarget | null {
  return current
}

/** Whether a call is LIVE — present and not yet ended or failed. */
export function readSosCallLive(): boolean {
  return isLive()
}

/** The vendor's low-end helmet: no camera, no media surface, so nothing to hold a call with. */
const CAP_TYPE_LOW_END = 3

/**
 * Whether this device can take a call at all.
 *
 * Keyed on what we reliably KNOW — that it is a helmet — and excluding only what we know cannot
 * work. It deliberately does NOT require `capType === 2`, and that distinction is the difference
 * between a working feature and an invisible one: `capType` is optional operator-supplied metadata
 * set at registration (`POST /robots`), not something the device reports. The firmware never sends
 * it — `cap_type` appears zero times in its dex, and the one in our login RESPONSE is a field we
 * send TO the device. So a helmet registered without it, which is most of them, would never be
 * offered a call and nothing on screen would explain why.
 *
 * `callSupport === 'unanswered'` is OUR record that a call was placed to this device and its
 * firmware never joined. Offering it again rings a helmet that has already demonstrated it cannot
 * answer, while an operator waits out the whole probe window.
 */
export function deviceCanTakeCall(device: CallableDevice | undefined): boolean {
  return callRefusalReason(device) === null
}

/** A device, as much of one as this decision needs. */
export interface CallableDevice {
  kind?: string
  capType?: number
  callSupport?: string
}

/**
 * WHY a device cannot be called — or null when it can.
 *
 * {@link deviceCanTakeCall} answers the yes/no the emergency popup asks; this answers the second
 * question a standing Call BUTTON has to answer, because the two refusals want opposite treatment.
 * A robot has no microphone and never will, so a Call control on it is noise and is not rendered.
 * A helmet that failed to answer its last call is a device an operator has every reason to expect a
 * button on — omitting it silently reads as a broken console, so it is shown and says why.
 *
 * One implementation for both, so the button can never offer a call the popup would refuse.
 */
export type CallRefusal = 'unknown_device' | 'not_a_helmet' | 'no_media_surface' | 'unanswered'

export function callRefusalReason(device: CallableDevice | undefined): CallRefusal | null {
  if (!device) return 'unknown_device'
  // A robot has no speaker and no microphone. `kind` is absent on older records, where a set
  // `capType` is the only other evidence it is a helmet at all.
  if (device.kind !== 'helmet' && device.capType === undefined) return 'not_a_helmet'
  if (device.capType === CAP_TYPE_LOW_END) return 'no_media_surface'
  if (device.callSupport === 'unanswered') return 'unanswered'

  return null
}
